import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Alert
} from '@mui/material';
import { USER_ROLES, USER_ROLE_DISPLAY_NAMES, UserRole } from '../../types/enums';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MultipleRoleSelector = ({ 
  selectedRoles = [], 
  onChange, 
  disabled = false,
  showLegacyWarning = false,
  label = "Roles",
  helperText 
}) => {
  const [roles, setRoles] = useState(selectedRoles);

  useEffect(() => {
    setRoles(selectedRoles);
  }, [selectedRoles]);

  const handleRoleChange = (event) => {
    const value = event.target.value;
    const newRoles = typeof value === 'string' ? value.split(',') : value;
    setRoles(newRoles);
    onChange(newRoles);
  };

  const handleDeleteRole = (roleToDelete) => {
    const newRoles = roles.filter(role => role !== roleToDelete);
    setRoles(newRoles);
    onChange(newRoles);
  };

  // Role hierarchy validation
  const validateRoleHierarchy = (selectedRoles) => {
    const warnings = [];
    
    if (selectedRoles.includes(UserRole.ADMIN) && selectedRoles.length > 1) {
      warnings.push("Admin role typically doesn't need additional roles as it has highest privileges");
    }
    
    if (selectedRoles.includes(UserRole.DOCTOR) && selectedRoles.includes(UserRole.NURSE)) {
      warnings.push("Doctor and Nurse roles overlap in clinical permissions");
    }

    return warnings;
  };

  const warnings = validateRoleHierarchy(roles);

  return (
    <Box>
      {showLegacyWarning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This user currently has a single role. Selecting multiple roles will upgrade them to the new multiple-role system.
        </Alert>
      )}

      <FormControl fullWidth disabled={disabled}>
        <InputLabel id="multiple-role-label">{label}</InputLabel>
        <Select
          labelId="multiple-role-label"
          multiple
          value={roles}
          onChange={handleRoleChange}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((role) => (
                <Chip
                  key={role}
                  label={USER_ROLE_DISPLAY_NAMES[role]}
                  size="small"
                  onDelete={disabled ? undefined : () => handleDeleteRole(role)}
                  sx={{ 
                    bgcolor: getChipColor(role),
                    color: 'white',
                    '& .MuiChip-deleteIcon': {
                      color: 'white'
                    }
                  }}
                />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
        >
          {USER_ROLES.map((role) => (
            <MenuItem key={role} value={role}>
              <Checkbox checked={roles.indexOf(role) > -1} />
              <ListItemText 
                primary={USER_ROLE_DISPLAY_NAMES[role]}
                secondary={getRoleDescription(role)}
              />
            </MenuItem>
          ))}
        </Select>
        
        {helperText && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {helperText}
          </Typography>
        )}
      </FormControl>

      {warnings.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {warnings.map((warning, index) => (
            <Alert key={index} severity="warning" sx={{ mt: 1 }}>
              {warning}
            </Alert>
          ))}
        </Box>
      )}

      {roles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Selected roles: {roles.map(role => USER_ROLE_DISPLAY_NAMES[role]).join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Helper function to get chip colors based on role
const getChipColor = (role) => {
  const colors = {
    [UserRole.ADMIN]: '#d32f2f',      // Red
    [UserRole.FACILITY_ADMIN]: '#f57c00', // Orange
    [UserRole.DOCTOR]: '#1976d2',     // Blue
    [UserRole.NURSE]: '#388e3c',      // Green
    [UserRole.USER]: '#616161'        // Grey
  };
  return colors[role] || '#616161';
};

// Helper function to get role descriptions
const getRoleDescription = (role) => {
  const descriptions = {
    [UserRole.ADMIN]: 'Full system access',
    [UserRole.FACILITY_ADMIN]: 'Facility management',
    [UserRole.DOCTOR]: 'Clinical care and procedures',
    [UserRole.NURSE]: 'Patient care and monitoring',
    [UserRole.USER]: 'Basic system access'
  };
  return descriptions[role];
};

export default MultipleRoleSelector; 